// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, EventEmitter, Output, Input, ViewChildren, ElementRef, QueryList, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators, UntypedFormArray } from '@angular/forms';
import { IPolicyControlCache, ProtectionLevel, IPolicyExtensions, ID3EPolicy, IClientPolicyDefaults, IPolicySection } from 'src/types/d3ePolicy';
import { ProtectionLevelBottomSheetComponent } from '../default-file-format/protection-level-bottom-sheet/protetction-level-bottom-sheet.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ImportExtsFoldersComponent } from 'src/app/dialogs/import-exts-folders/import-exts-folders.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ALPHA_NUMERIC_REGEXP, NO_DOT_EXE_OR_DLL_REGEXP } from '../../../../types/common';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-custom-file-format',
    templateUrl: './custom-file-format.component.html',
    styleUrls: ['./custom-file-format.component.scss'],
    standalone: false
})
export class CustomFileFormatComponent implements OnChanges {
  @Input()d3ePolicy: ID3EPolicy;
  @Input()clientPolicyDefaults: IClientPolicyDefaults;
  @Input()allowAlwaysOn: boolean;

  @Output()policyExtensionsChanged: EventEmitter<IPolicyExtensions> = new EventEmitter<IPolicyExtensions>();
  @Output()extensionErrorStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()import: EventEmitter<IPolicyExtensions> = new EventEmitter<IPolicyExtensions>();
  
  @ViewChildren('extensionInputs', { read: ElementRef}) extensionInputs: QueryList<ElementRef>;
  
  policyExtensions: IPolicyExtensions = {disabledExtensions: [], protectedExtensions: [], lockedExtensions: []};
  fileFormatFormGroup: UntypedFormGroup;
  fileFormats: UntypedFormArray; // Used in html
  toggleGroupAppearance: 'legacy' | 'standard' = 'standard';
  cachedControlValues: IPolicyControlCache[] = [];
  extensionErrors: string[] = [];
  defaultExtensions: string[] = [];
  importErrors = 0;
  importDefaultExtensions : IPolicyExtensions = {disabledExtensions: [], protectedExtensions: [], lockedExtensions: []};

  fTopicDescription: string;
  fErrorInvalidExt: string;
  fErrorOnlyAlpha: string;
  fErrorExtExists: string;
  fErrorExtLength: string;
  fErrorExtExistsOne: string;
  fErrorExtExistsTwo: string;

  constructor(
    private bottomSheet: MatBottomSheet,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.translate.get(['TOPIC_DESCRIPTION', 'ERROR_INVALID_EXT', 'ERROR_ONLY_ALPHA',
      'ERROR_EXT_EXISTS', 'ERROR_EXT_LENGTH', 'ERROR_EXT_EXISTS_ONE', 'ERROR_EXT_EXISTS_TWO']).subscribe(vals => {
        this.fTopicDescription = vals['TOPIC_DESCRIPTION'];
        this.fErrorInvalidExt = vals['ERROR_INVALID_EXT'];
        this.fErrorOnlyAlpha = vals['ERROR_ONLY_ALPHA'];
        this.fErrorExtExists = vals['ERROR_EXT_EXISTS'];
        this.fErrorExtLength = vals['ERROR_EXT_LENGTH'];
        this.fErrorExtExistsOne = vals['ERROR_EXT_EXISTS_ONE'];
        this.fErrorExtExistsTwo = vals['ERROR_EXT_EXISTS_TWO'];
    })
    this.allowAlwaysOn = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.importErrors = 0;
    if (changes.d3ePolicy && changes.d3ePolicy.currentValue) {
      this.cachedControlValues = [];

      if (this.fileFormatFormGroup) {
        const fileFormats = this.fileFormatFormGroup.get('fileFormats') as UntypedFormArray;
        const length = fileFormats.length;
        for (let i = 0; i < length; i++) {
          fileFormats.removeAt(0);
        }
        this.extensionErrors = [];
      }

      if (changes.allowAlwaysOn && changes.allowAlwaysOn.currentValue) {
        this.allowAlwaysOn = changes.allowAlwaysOn.currentValue;
      }
    
      const customExtensions = this.filterExtensions(changes.d3ePolicy.currentValue);
      this.updatePolicyExtensions(customExtensions);
    }
  }

  onInputChange(event, control, index) {
    const extension = control.value.path.trim();
    this.cachedControlValues[index].newValue = extension;
    this.validateExtensions(extension, index);

    let protectionLevel: ProtectionLevel = 'medium';
    const extensionSettings = control.value.setting.split('-');
    if (extensionSettings.length === 2) {
      protectionLevel = extensionSettings[0] as ProtectionLevel;

      // Remove previous value from the policyExtensions
      this.removeExtensionFromPolicy(this.cachedControlValues[index].setting, index);

      if (protectionLevel === 'medium') {
        // add new value
        this.policyExtensions.protectedExtensions.push(extension);
        this.cachedControlValues[index].setting = 'medium';

      } else if (protectionLevel === 'high') {
        // add new value
        this.policyExtensions.lockedExtensions.push(extension);
        this.cachedControlValues[index].setting = 'high';

      } else {
        // add new value
        this.policyExtensions.disabledExtensions.push(extension);
        this.cachedControlValues[index].setting = 'none';
      }
    }
    
    this.cachedControlValues[index].originalValue = extension;
    this.cachedControlValues[index].newValue = extension;

    control.patchValue({
      path: extension,
      setting: `${protectionLevel}-${index}`
    });

    this.controls.forEach((c, i) => {
      if (index !== i) {
        const path = c.get('path').value || '';
        this.validateExtensions(path, i);
      }
    });

    this.policyExtensionsChanged.emit(this.policyExtensions);
  }

  onFocusIn(event, index) {
    this.controls.forEach((c, i) => {
      if (index !== i) {
        const path = c.get('path').value || '';
        this.validateExtensions(path, i);
      }
    });
  }

  onFocusOut(event, index) {
    this.validateExtensions(this.cachedControlValues[index].newValue, index);

  }

  hasExtensionError(i) {
    let hasError = false;
    if (this.extensionErrors[i] !== null) {
      hasError = true;
    }
    return hasError;
  }

  getEmptyPolicy() {
    const settings = this.clientPolicyDefaults.sections.find(s => s.name.toLocaleLowerCase() === 'settings').groups[0].settings;
    const defaultSyncInterval = settings.find(s => s.name.toLowerCase() === 'syncinterval').value;
    const defaultSyncIntervalMetered = settings.find(s => s.name.toLowerCase() === 'syncintervalmetered').value;
    const defaultLicenseInterval = settings.find(s => s.name.toLowerCase() === 'licenseinterval').value;
    const defaultLicenseIntervalMetered = settings.find(s => s.name.toLowerCase() === 'licenseintervalmetered').value;
    const defaultPreAuthFileAccessCount = settings.find(s => s.name.toLowerCase() === 'preauthfileaccesscount').value;

    return {
      disabledExts: [],
      protectedExts: [],
      lockedExts: [],
      disabledDirs: [],
      protectedDirs: [],
      lockedDirs: [],
      encryptedProtectedDirs: [],
      encryptedLockedDirs: [],
      deceptionFiles: [],
      protectedVolumes: [],
      sensors: [],
      syncInterval: +defaultSyncInterval,
      syncIntervalMetered: +defaultSyncIntervalMetered,
      licenseInterval: +defaultLicenseInterval,
      licenseIntervalMetered: +defaultLicenseIntervalMetered,
      preAuthFileAccessCount: +defaultPreAuthFileAccessCount
    };
  }
  
  filterExtensions(d3ePolicy: ID3EPolicy): IPolicyExtensions {
    if (Object.keys(d3ePolicy).length <= 1) {
      d3ePolicy = this.getEmptyPolicy();
    }

    const updatedExtensions = {
      'disabledExtensions': [...new Set(d3ePolicy.disabledExts ?? [])],
      'protectedExtensions': [...new Set(d3ePolicy.protectedExts ?? [])],
      'lockedExtensions': [...new Set(d3ePolicy.lockedExts ?? [])]
    };
    
    const defaultSections: IPolicySection[] = this.clientPolicyDefaults.sections.filter(s => {
      return !['custom file types', 'folders', 'settings', 'known good'].includes(s.name.toLowerCase());
      }
    );

    this.defaultExtensions = defaultSections.reduce(
      (arr, elem) => {
        if (elem.groups) {
          for (const file of elem.groups) {
            arr.push(...file.extensions.map(e => e.extension));
          }
        }
        return arr;
      }, [] as string[]
    );

    return  {
      'disabledExtensions': updatedExtensions.disabledExtensions.filter(e => 
        !this.defaultExtensions.includes(e)) || [],
      'protectedExtensions': updatedExtensions.protectedExtensions.filter(e => 
        !this.defaultExtensions.includes(e)) || [],
      'lockedExtensions': updatedExtensions.lockedExtensions.filter(e => 
        !this.defaultExtensions.includes(e)) || []
    } as IPolicyExtensions;

  }

  updatePolicyExtensions(policyExtensions: IPolicyExtensions): void {

    this.policyExtensions = policyExtensions;

    if (!this.policyExtensions.disabledExtensions) {
      this.policyExtensions.disabledExtensions = [];
    }

    if (this.policyExtensions.protectedExtensions.length === 0 
      && this.policyExtensions.lockedExtensions.length === 0
      && this.policyExtensions.disabledExtensions.length === 0) {
        this.fileFormatFormGroup = this.formBuilder.group({
          fileFormats: this.formBuilder.array([this.createFileFormatGroup('', 'medium-0', false)])
        });
        this.cachedControlValues = [];
        this.cachedControlValues.push({originalValue: '', newValue: '', editMode: false, setting: 'medium', encryptedSetting: 'none-encrypted'});
    } else {

      const allExtensions: {'ext': string, 'setting': ProtectionLevel}[] = [];

      this.policyExtensions.disabledExtensions.forEach(e => {
        allExtensions.push({'ext': e, 'setting': 'none'});
      });

      this.policyExtensions.protectedExtensions.forEach(e => {
        allExtensions.push({'ext': e, 'setting': 'medium'});
      });
      
      this.policyExtensions.lockedExtensions.forEach(e => {
        allExtensions.push({'ext': e, 'setting': 'high'});
      });

      allExtensions.sort((a, b) => a.ext.localeCompare(b.ext));

      allExtensions.forEach((value, index) => {
        const path = value.ext;
        const protectionLevel = value.setting;
        if (this.fileFormatFormGroup) {
          this.addFileFormatFormGroup(path, `${protectionLevel}-${index}`);
        } else {
          this.fileFormatFormGroup = this.formBuilder.group({
            fileFormats: this.formBuilder.array([this.createFileFormatGroup(path, `${protectionLevel}-${index}`)])
          });
        }
        this.cachedControlValues.push({originalValue: path, newValue: path, editMode: false, setting: value.setting, encryptedSetting: 'none-encrypted'});

      });
    }
  }

  get controls()  {
    return (this.fileFormatFormGroup.get('fileFormats') as UntypedFormArray).controls;
  }

  onToggleChanged(event, control, index) {
    const extensionSettings = event.value.split('-');

    if (extensionSettings.length === 2) {
      const protectionLevel = extensionSettings[0] as ProtectionLevel;
      control.patchValue({
        setting: event.value
      });

      this.updateExtensionValue(this.cachedControlValues[index].setting, protectionLevel, index);

      this.controls.forEach((c, i) => {
        const path = c.get('path').value || '';
        this.validateExtensions(path, i);
      });
      
    }
  }

  updateExtensionValue(previousProtectionLevel: ProtectionLevel, newProtectionLevel: ProtectionLevel, index: number): void {
    if (index >= 0 && index < this.controls.length) {
      const path = this.controls[index].get('path').value.trim(); 

      if (path.length > 0) {
        this.removeExtensionFromPolicy(previousProtectionLevel, index);
        switch (newProtectionLevel) {
          case 'medium':
            this.policyExtensions.protectedExtensions.push(path);
            break;
          case 'high':
            this.policyExtensions.lockedExtensions.push(path);
            break;
          default:  // case 'none'
            this.policyExtensions.disabledExtensions.push(path);
            break;
        }
      }

      this.cachedControlValues[index].setting = newProtectionLevel;

      this.policyExtensionsChanged.emit(this.policyExtensions);
    }
  }

  removeExtensionFromPolicy(protectionLevel: ProtectionLevel, index: number) {
    switch (protectionLevel) {
      case 'none':
        const disabledIndex = this.policyExtensions.disabledExtensions.findIndex(p => 
          p.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (disabledIndex >= 0) {
          this.policyExtensions.disabledExtensions.splice(disabledIndex, 1);
        }
        break;
      case 'medium':
        const protectedIndex = this.policyExtensions.protectedExtensions.findIndex(p => 
          p.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (protectedIndex >= 0) {
          this.policyExtensions.protectedExtensions.splice(protectedIndex, 1);
        }
        break;
      case 'high':
        const lockedIndex = this.policyExtensions.lockedExtensions.findIndex(p => 
          p.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (lockedIndex >= 0) {
          this.policyExtensions.lockedExtensions.splice(lockedIndex, 1);
        }
        break;
    }
  }


  public addFileFormatFormGroup(path: string = '', setting: string = '') {
    const fileFormats = this.fileFormatFormGroup.get('fileFormats') as UntypedFormArray;
    const extensionSetting = setting.trim() !== '' ? setting : `medium-${fileFormats.length}`;
    
    // Only one new empty extension at a time.
    if (fileFormats.value.some(v => v.path === '')) {
      return;
    }

    fileFormats.push(this.createFileFormatGroup(path, extensionSetting));

  }

  public onAddExtensionClick() {
    const path = '';
    const protectionLevel = 'medium';
    const fileFormats = this.fileFormatFormGroup.get('fileFormats') as UntypedFormArray;
    const extensionSetting = `${protectionLevel}-${fileFormats.length}`;

    if (fileFormats.value.some(v => v.path === '')) {
      // TODO: Warn user they have a new extension that needs a path before creating a new extension.
      return;
    }

    fileFormats.push(this.createFileFormatGroup(path, extensionSetting, false));
    this.cachedControlValues.push({originalValue: path, newValue: path, editMode: true, setting: protectionLevel});
    // Make this dirty so we see enable the cancel/save buttons
    this.policyExtensionsChanged.emit(this.policyExtensions);
  }

  removeExtensionRow(path, index: number) {
    const fileFormats = this.fileFormatFormGroup.get('fileFormats') as UntypedFormArray;
    const previousProtectionLevel = this.cachedControlValues[index].setting;

    if (fileFormats.length > 0 && index < fileFormats.length) {
      fileFormats.removeAt(index);
      
      this.removeExtensionFromPolicy(previousProtectionLevel, index);
      this.cachedControlValues.splice(index, 1);

      if (fileFormats.length === 0) {
        this.onAddExtensionClick();
      }

      this.extensionErrors.splice(index, 1);

      this.controls.forEach((c, i) => {
        const p = c.get('path').value || '';
        this.validateExtensions(p, i);
      });

      this.policyExtensionsChanged.emit(this.policyExtensions);
    }
  }

  private createFileFormatGroup(path: string, settings: string, validate = true) {
    const length = this.extensionErrors.push(null);
    if (validate) {
      this.validateExtensions(path, length - 1);
    }

    return new UntypedFormGroup({
      'path': new UntypedFormControl(path.trim(), Validators.nullValidator),
      'setting': new UntypedFormControl({value: settings, disabled: false}, Validators.nullValidator)
    });
  }

  validateExtensions(value: String, index: number) {
    let error = null;
    const extension = value.trim();

    if (NO_DOT_EXE_OR_DLL_REGEXP.test(extension) ) {
      error = this.fErrorInvalidExt;
    }


    if (!(this.cachedControlValues.length === 1 && extension === '') && !ALPHA_NUMERIC_REGEXP.test(extension) ) {
      error = this.fErrorOnlyAlpha;
    }

    if (this.cachedControlValues.length > index) {
      this.cachedControlValues.forEach((c, i) => {
        if (c.newValue.toLocaleLowerCase() === extension.toLocaleLowerCase() && i !== index) {
          error = this.fErrorExtExists;
        }
      });
    }

    const defaultSections: IPolicySection[] = this.clientPolicyDefaults.sections.filter(s => {
      return !['custom file types', 'folders', 'settings', 'known good'].includes(s.name.toLowerCase());
      }
    );

    defaultSections.forEach(g => {
      if (g.groups) {
        g.groups.forEach(f => {
          const extensionExists = f.extensions.some(e => e.extension.toLocaleLowerCase() === extension.toLocaleLowerCase());
          if (extensionExists) {
            error = `${this.fErrorExtExistsOne} '${g.name}' ${this.fErrorExtExistsTwo} '${f.name}'.`;
          }
        });
      }
    });

    if ((extension.length < 1 || extension.length > 260) && !(this.cachedControlValues.length === 1 && extension === '')) {
      error = this.fErrorExtLength;
    }
    
    this.extensionErrors[index] = error;
    const hasError = !this.extensionErrors.every(c => c === null);
    this.extensionErrorStateChanged.emit(hasError);

  }

  onProtectionLevelClick() {
    this.bottomSheet.open(ProtectionLevelBottomSheetComponent, {
      data: {
        topicDescription: this.fTopicDescription
      },
      disableClose: false,
      closeOnNavigation: true,
      autoFocus: true,
      hasBackdrop: true,
    });
  }

  onImportClick() {
    const dialogRef = this.dialog.open(ImportExtsFoldersComponent, {
      width: '650px',
      height: '400px',
      data: {
        openedByExts: true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        result[0].forEach(newFolder => {
          const isValidProtectionLevel = this.isValidProtectionLevel(newFolder.setting);
          if (isValidProtectionLevel) {
            const isDefaultExt = this.importIsDefaultExt(newFolder.value, newFolder.setting);
            if (!isDefaultExt) {
              this.onAddImportExtension(newFolder.value, newFolder.setting);
            }
          }
          else {
            this.importErrors++;
          }
        });
        this.importErrors = this.importErrors + result[1];
        this.import.emit(this.importDefaultExtensions);
        this.importDefaultExtensions = {disabledExtensions: [], protectedExtensions: [], lockedExtensions: []};
      }
    });
  }

  isValidProtectionLevel(setting:string) {
    if (setting === 'none') {
      return true;
    }
    return setting === 'dynamic';
  }

  public getImportProtectionLevel(setting: string) {
    if (setting === 'none') {
      return `none`;
    }
    if (setting === 'dynamic') {
      return `medium`;
    }
    if (setting === 'always on') {
      return `high`;
    }
    return `medium`;
  }

  public onAddImportExtension(value: string, setting: string) {
    const path = value;
    const protectionLevel = this.getImportProtectionLevel(setting);
    const fileFormats = this.fileFormatFormGroup.get('fileFormats') as UntypedFormArray;
    const newIndex = fileFormats.length;
    const extensionSetting = protectionLevel + `-${fileFormats.length}`;
    let found = false;

    const rawFolders = fileFormats.getRawValue();
    const length = fileFormats.length;

    for (let i = 0; i < length; i++) {
      const folderPath = rawFolders[i]['path'];
      if (folderPath === value) {
        found = true;
        const protectionLevel = this.getImportProtectionLevel(setting) as ProtectionLevel;
        this.controls[i].patchValue({
          setting: protectionLevel + `-${i}`
        });
        this.updateExtensionValue(this.cachedControlValues[i].setting, this.getImportProtectionLevel(setting), i);
        this.validateExtensions(path, i);
      }
    }

    if (!found) {
      fileFormats.push(this.createFileFormatGroup(path, extensionSetting, false));
      this.cachedControlValues.push({originalValue: path, newValue: path, editMode: true, setting: protectionLevel});
      this.validateExtensions(path, newIndex);

      switch (protectionLevel) {
        case 'medium':
          this.policyExtensions.protectedExtensions.push(path);
          break;
        case 'high':
          this.policyExtensions.lockedExtensions.push(path);
          break;
        default:  // case 'none'
          this.policyExtensions.disabledExtensions.push(path);
          break;
      }

      this.policyExtensionsChanged.emit(this.policyExtensions);
    }
  }

  public importIsDefaultExt(value: string, setting: string) : boolean {
    let found = false;

    const defaultSections: IPolicySection[] = this.clientPolicyDefaults.sections.filter(s => {
      return !['custom file types', 'folders', 'settings', 'known good'].includes(s.name.toLowerCase());
      }
    );

    defaultSections.forEach(g => {
      if (g.groups) {
        g.groups.forEach(f => {
          f.extensions.forEach(e => {
            if (e.extension == value){
              const protectionLevel = this.getImportProtectionLevel(setting);
              switch (protectionLevel) {
                case 'medium':
                  this.importDefaultExtensions.protectedExtensions.push(value);
                  break;
                default:  // case 'none'
                  this.importDefaultExtensions.disabledExtensions.push(value);
                  break;
              }
              found = true;
            }
          });
        });
      }
    });
    return found;
  }
}
